import { useState, useEffect } from 'react';
import { api, formatCurrency } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { AlertTriangle, Plus, Check, Send, Trash2, X, ChevronDown, ChevronUp, Package, DollarSign, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  a_reclamer: { label: 'À réclamer', color: 'bg-orange-500/15 text-orange-400' },
  reclame: { label: 'Réclamé', color: 'bg-blue-500/15 text-blue-400' },
  recu: { label: 'Reçu', color: 'bg-lemas-green/15 text-lemas-green' },
};

const AvoirsSection = () => {
  const [avoirs, setAvoirs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({
    fournisseur: '', date_facture: '', note: '',
    produits: [{ nom: '', quantite: '', prix_unitaire: '', motif: 'Non livré' }]
  });

  const fetchAvoirs = async () => {
    try {
      const res = await api.get('/avoirs');
      setAvoirs(res.data);
    } catch {}
  };

  useEffect(() => { fetchAvoirs(); }, []);

  const addProductLine = () => {
    setForm(f => ({
      ...f,
      produits: [...f.produits, { nom: '', quantite: '', prix_unitaire: '', motif: 'Non livré' }]
    }));
  };

  const updateProduct = (idx, field, value) => {
    setForm(f => {
      const p = [...f.produits];
      p[idx] = { ...p[idx], [field]: value };
      return { ...f, produits: p };
    });
  };

  const removeProduct = (idx) => {
    setForm(f => ({ ...f, produits: f.produits.filter((_, i) => i !== idx) }));
  };

  const submitAvoir = async () => {
    if (!form.fournisseur || form.produits.length === 0) {
      toast.error('Remplir le fournisseur et au moins un produit');
      return;
    }
    const produits = form.produits
      .filter(p => p.nom)
      .map(p => ({
        nom: p.nom,
        quantite: parseFloat(p.quantite) || 1,
        prix_unitaire: parseFloat(p.prix_unitaire) || 0,
        motif: p.motif || 'Non livré'
      }));
    const montant = produits.reduce((s, p) => s + p.quantite * p.prix_unitaire, 0);

    try {
      await api.post('/avoirs', {
        fournisseur: form.fournisseur,
        date_facture: form.date_facture || new Date().toISOString().split('T')[0],
        produits,
        montant_total: montant,
        note: form.note
      });
      toast.success('Avoir créé');
      setForm({ fournisseur: '', date_facture: '', note: '', produits: [{ nom: '', quantite: '', prix_unitaire: '', motif: 'Non livré' }] });
      setShowForm(false);
      fetchAvoirs();
    } catch { toast.error('Erreur'); }
  };

  const reclamer = async (id) => {
    try {
      await api.put(`/avoirs/${id}/reclamer`);
      toast.success('Avoir réclamé');
      fetchAvoirs();
    } catch { toast.error('Erreur'); }
  };

  const recevoir = async (id, montant) => {
    try {
      await api.put(`/avoirs/${id}/recu`, { montant_recu: montant });
      toast.success('Avoir reçu');
      fetchAvoirs();
    } catch { toast.error('Erreur'); }
  };

  const deleteAvoir = async (id) => {
    try {
      await api.delete(`/avoirs/${id}`);
      toast.success('Avoir supprimé');
      fetchAvoirs();
    } catch { toast.error('Erreur'); }
  };

  const aReclamer = avoirs.filter(a => a.status === 'a_reclamer');
  const reclames = avoirs.filter(a => a.status === 'reclame');
  const recus = avoirs.filter(a => a.status === 'recu');
  const totalEnCours = [...aReclamer, ...reclames].reduce((s, a) => s + a.montant_total, 0);

  return (
    <div className="kpi-card border-orange-400/20" data-testid="avoirs-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle size={18} className="text-orange-400" />
          <h3 className="font-title text-lg">Avoirs à réclamer</h3>
          {totalEnCours > 0 && (
            <span className="bg-orange-400/20 text-orange-400 text-xs font-mono px-2 py-0.5 rounded-full">
              {aReclamer.length + reclames.length} · {formatCurrency(totalEnCours)}
            </span>
          )}
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          size="sm"
          className={showForm ? "bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text))]" : "bg-orange-400/20 text-orange-400 hover:bg-orange-400/30"}
          data-testid="add-avoir-btn"
        >
          {showForm ? <X size={14} /> : <><Plus size={14} className="mr-1" /> Nouveau</>}
        </Button>
      </div>

      {/* Add form */}
      {showForm && (
        <div className="bg-lemas-bg/60 rounded-lg p-4 mb-4 space-y-3 animate-slide-up" data-testid="avoir-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Fournisseur</label>
              <Input value={form.fournisseur} onChange={e => setForm(f => ({ ...f, fournisseur: e.target.value }))} placeholder="ex: Metro" className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" data-testid="avoir-fournisseur" />
            </div>
            <div>
              <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Date facture</label>
              <Input type="date" value={form.date_facture} onChange={e => setForm(f => ({ ...f, date_facture: e.target.value }))} className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] font-mono" data-testid="avoir-date" />
            </div>
          </div>

          <p className="text-xs text-[rgb(var(--lm-text-secondary))] font-medium">Produits manquants / non livrés</p>
          {form.produits.map((p, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Input value={p.nom} onChange={e => updateProduct(idx, 'nom', e.target.value)} placeholder="Produit" className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] flex-1" />
              <Input type="number" value={p.quantite} onChange={e => updateProduct(idx, 'quantite', e.target.value)} placeholder="Qté" className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] w-16 font-mono text-center" />
              <Input type="number" value={p.prix_unitaire} onChange={e => updateProduct(idx, 'prix_unitaire', e.target.value)} placeholder="€/u" className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] w-20 font-mono text-center" />
              <select value={p.motif} onChange={e => updateProduct(idx, 'motif', e.target.value)} className="bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] rounded-md p-2 text-xs w-28">
                <option value="Non livré">Non livré</option>
                <option value="Abîmé">Abîmé</option>
                <option value="Erreur">Erreur</option>
                <option value="Périmé">Périmé</option>
              </select>
              {form.produits.length > 1 && (
                <button onClick={() => removeProduct(idx)} className="text-[rgb(var(--lm-text-muted))] hover:text-red-400"><X size={14} /></button>
              )}
            </div>
          ))}
          <button onClick={addProductLine} className="text-xs text-orange-400 hover:text-orange-300">+ Ajouter un produit</button>

          <Input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="Note (optionnel)" className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-sm" />

          <Button onClick={submitAvoir} className="w-full bg-orange-500 hover:bg-orange-600 text-[rgb(var(--lm-text))]" data-testid="submit-avoir">
            <AlertTriangle size={14} className="mr-2" /> Créer l'avoir
          </Button>
        </div>
      )}

      {/* Avoirs list */}
      {avoirs.length > 0 ? (
        <div className="space-y-2">
          {avoirs.filter(a => a.status !== 'recu').map(a => {
            const cfg = STATUS_CONFIG[a.status] || STATUS_CONFIG.a_reclamer;
            return (
              <div key={a.id} className="rounded-lg bg-lemas-bg/40 border border-[rgb(var(--lm-border))] overflow-hidden" data-testid={`avoir-${a.id}`}>
                <div className="flex items-center justify-between p-3 cursor-pointer" onClick={() => setExpanded(expanded === a.id ? null : a.id)}>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${cfg.color}`}>{cfg.label}</span>
                    <span className="text-sm font-medium truncate">{a.fournisseur}</span>
                    <span className="font-mono text-xs text-[rgb(var(--lm-text-muted))]">{a.date_facture}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <span className="font-mono text-orange-400">{formatCurrency(a.montant_total)}</span>
                    <span className="text-xs text-[rgb(var(--lm-text-muted))]">{a.produits?.length || 0} prod.</span>
                    {expanded === a.id ? <ChevronUp size={14} className="text-[rgb(var(--lm-text-muted))]" /> : <ChevronDown size={14} className="text-[rgb(var(--lm-text-muted))]" />}
                  </div>
                </div>
                {expanded === a.id && (
                  <div className="px-3 pb-3 space-y-2 animate-slide-up">
                    {a.produits?.map((p, i) => (
                      <div key={i} className="flex items-center justify-between text-sm bg-lemas-bg/40 rounded p-2">
                        <div>
                          <span className="font-medium">{p.nom}</span>
                          <span className="text-xs text-[rgb(var(--lm-text-muted))] ml-2">{p.motif}</span>
                        </div>
                        <span className="font-mono text-xs">{p.quantite} × {formatCurrency(p.prix_unitaire)}</span>
                      </div>
                    ))}
                    {a.note && <p className="text-xs text-[rgb(var(--lm-text-secondary))] italic">{a.note}</p>}
                    <div className="flex gap-2 pt-1">
                      {a.status === 'a_reclamer' && (
                        <Button size="sm" onClick={() => reclamer(a.id)} className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 text-xs">
                          <Send size={12} className="mr-1" /> Réclamé
                        </Button>
                      )}
                      {a.status === 'reclame' && (
                        <Button size="sm" onClick={() => recevoir(a.id, a.montant_total)} className="bg-lemas-green/20 text-lemas-green hover:bg-lemas-green/30 text-xs">
                          <Check size={12} className="mr-1" /> Avoir reçu
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => deleteAvoir(a.id)} className="text-lemas-danger text-xs">
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {/* Received avoirs collapsed */}
          {recus.length > 0 && (
            <div className="pt-2 border-t border-[rgb(var(--lm-border))]">
              <p className="text-xs text-[rgb(var(--lm-text-muted))] mb-1">{recus.length} avoir(s) reçu(s) — {formatCurrency(recus.reduce((s, a) => s + (a.montant_recu || a.montant_total), 0))}</p>
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-[rgb(var(--lm-text-muted))] text-center py-2">Aucun avoir en cours</p>
      )}
    </div>
  );
};

const ConsignesSection = () => {
  const [consignes, setConsignes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState('en_cours');
  const [form, setForm] = useState({ fournisseur: 'Metro', type_consigne: '', quantite: '1', prix_unitaire: '', note: '' });

  const fetchConsignes = async () => {
    try {
      const res = await api.get('/consignes');
      setConsignes(res.data);
    } catch {}
  };

  useEffect(() => { fetchConsignes(); }, []);

  const submitConsigne = async () => {
    if (!form.type_consigne) { toast.error('Indiquer le type de consigne'); return; }
    try {
      await api.post('/consignes', {
        fournisseur: form.fournisseur,
        type_consigne: form.type_consigne,
        quantite: parseInt(form.quantite) || 1,
        prix_unitaire: parseFloat(form.prix_unitaire) || 0,
        note: form.note
      });
      toast.success('Consigne ajoutée');
      setForm({ fournisseur: 'Metro', type_consigne: '', quantite: '1', prix_unitaire: '', note: '' });
      setShowForm(false);
      fetchConsignes();
    } catch { toast.error('Erreur'); }
  };

  const retourner = async (id) => {
    try {
      await api.put(`/consignes/${id}/retourner`);
      toast.success('Consigne marquée comme reprise');
      fetchConsignes();
    } catch { toast.error('Erreur'); }
  };

  const rembourser = async (id) => {
    try {
      await api.put(`/consignes/${id}/rembourser`);
      toast.success('Consigne remboursée');
      fetchConsignes();
    } catch { toast.error('Erreur'); }
  };

  const deleteConsigne = async (id) => {
    try {
      await api.delete(`/consignes/${id}`);
      toast.success('Consigne supprimée');
      fetchConsignes();
    } catch { toast.error('Erreur'); }
  };

  const STATUS_CONSIGNE = {
    a_retourner: { label: 'A retourner', color: 'bg-orange-500/15 text-orange-400', icon: Package },
    repris: { label: 'Repris', color: 'bg-blue-500/15 text-blue-400', icon: RotateCcw },
    rembourse: { label: 'Remboursé', color: 'bg-lemas-green/15 text-lemas-green', icon: Check },
  };

  const aRetourner = consignes.filter(c => c.status === 'a_retourner');
  const repris = consignes.filter(c => c.status === 'repris');
  const rembourses = consignes.filter(c => c.status === 'rembourse');
  const enCours = [...aRetourner, ...repris];
  const totalEnCours = enCours.reduce((s, c) => s + c.montant_total, 0);
  const filteredConsignes = filter === 'en_cours' ? enCours : rembourses;

  return (
    <div className="kpi-card border-blue-400/20" data-testid="consignes-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Package size={18} className="text-blue-400" />
          <h3 className="font-title text-lg">Consignes</h3>
          {enCours.length > 0 && (
            <span className="bg-blue-400/20 text-blue-400 text-xs font-mono px-2 py-0.5 rounded-full">
              {enCours.length} · {formatCurrency(totalEnCours)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-lemas-bg/60 rounded-lg p-0.5 text-[10px]">
            <button onClick={() => setFilter('en_cours')} className={`px-2 py-1 rounded ${filter === 'en_cours' ? 'bg-blue-500/20 text-blue-400' : 'text-[rgb(var(--lm-text-muted))]'}`} data-testid="filter-en-cours">En cours ({enCours.length})</button>
            <button onClick={() => setFilter('rembourse')} className={`px-2 py-1 rounded ${filter === 'rembourse' ? 'bg-lemas-green/20 text-lemas-green' : 'text-[rgb(var(--lm-text-muted))]'}`} data-testid="filter-rembourse">Remboursés ({rembourses.length})</button>
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            size="sm"
            className={showForm ? "bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text))]" : "bg-blue-400/20 text-blue-400 hover:bg-blue-400/30"}
            data-testid="add-consigne-btn"
          >
            {showForm ? <X size={14} /> : <><Plus size={14} className="mr-1" /> Ajouter</>}
          </Button>
        </div>
      </div>

      {showForm && (
        <div className="bg-lemas-bg/60 rounded-lg p-4 mb-4 space-y-3 animate-slide-up" data-testid="consigne-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Fournisseur</label>
              <Input value={form.fournisseur} onChange={e => setForm(f => ({ ...f, fournisseur: e.target.value }))} className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" data-testid="consigne-fournisseur" />
            </div>
            <div>
              <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Type</label>
              <select value={form.type_consigne} onChange={e => setForm(f => ({ ...f, type_consigne: e.target.value }))} className="w-full bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] rounded-md p-2 text-sm" data-testid="consigne-type">
                <option value="">Choisir...</option>
                <option value="Bac plastique">Bac plastique</option>
                <option value="Caisse bois">Caisse bois</option>
                <option value="Palette">Palette</option>
                <option value="Roll">Roll</option>
                <option value="Fût">Fût</option>
                <option value="Bouteille consignée">Bouteille consignée</option>
                <option value="Autre">Autre</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Quantité</label>
              <Input type="number" value={form.quantite} onChange={e => setForm(f => ({ ...f, quantite: e.target.value }))} className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] font-mono" data-testid="consigne-quantite" />
            </div>
            <div>
              <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Prix unitaire</label>
              <Input type="number" value={form.prix_unitaire} onChange={e => setForm(f => ({ ...f, prix_unitaire: e.target.value }))} placeholder="0" className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] font-mono" data-testid="consigne-prix" />
            </div>
          </div>
          <Input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="Note (optionnel)" className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-sm" />
          <Button onClick={submitConsigne} className="w-full bg-blue-500 hover:bg-blue-600 text-white" data-testid="submit-consigne">
            <Package size={14} className="mr-2" /> Ajouter la consigne
          </Button>
        </div>
      )}

      {filteredConsignes.length > 0 ? (
        <div className="space-y-2">
          {filteredConsignes.map(c => {
            const cfg = STATUS_CONSIGNE[c.status] || STATUS_CONSIGNE.a_retourner;
            const StatusIcon = cfg.icon;
            return (
              <div key={c.id} className="flex items-center justify-between p-3 rounded-lg bg-lemas-bg/40 border border-[rgb(var(--lm-border))]" data-testid={`consigne-${c.id}`}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1 ${cfg.color}`}>
                    <StatusIcon size={10} />{cfg.label}
                  </span>
                  <span className="bg-blue-400/15 text-blue-400 text-xs font-mono px-2 py-0.5 rounded">{c.quantite}x</span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{c.type_consigne}</span>
                      <span className="text-xs text-[rgb(var(--lm-text-muted))]">{c.fournisseur}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[rgb(var(--lm-text-muted))] font-mono">{c.date_achat}</span>
                      {c.facture_id && <span className="text-[9px] px-1 py-0.5 rounded-sm bg-lemas-gold/10 text-lemas-gold">Auto</span>}
                      {c.note && <span className="text-[10px] text-[rgb(var(--lm-text-secondary))] italic truncate max-w-[120px]">{c.note}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <span className="font-mono text-blue-400">{formatCurrency(c.montant_total)}</span>
                  {c.status === 'a_retourner' && (
                    <Button size="sm" variant="ghost" onClick={() => retourner(c.id)} className="text-blue-400 hover:bg-blue-400/10 text-xs" data-testid={`repris-${c.id}`}>
                      <RotateCcw size={12} className="mr-1" /> Repris
                    </Button>
                  )}
                  {c.status === 'repris' && (
                    <Button size="sm" variant="ghost" onClick={() => rembourser(c.id)} className="text-lemas-green hover:bg-lemas-green/10 text-xs" data-testid={`rembourser-${c.id}`}>
                      <DollarSign size={12} className="mr-1" /> Remboursé
                    </Button>
                  )}
                  {c.status !== 'rembourse' && (
                    <Button size="sm" variant="ghost" onClick={() => deleteConsigne(c.id)} className="text-lemas-danger">
                      <Trash2 size={12} />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-[rgb(var(--lm-text-muted))] text-center py-2">
          {filter === 'en_cours' ? 'Aucune consigne en cours' : 'Aucune consigne remboursée'}
        </p>
      )}
    </div>
  );
};

export { AvoirsSection, ConsignesSection };
